import React, { useState, useEffect } from 'react';
import { css } from 'emotion';
import { connect } from 'react-redux';
import { isEmpty, cloneDeep } from 'lodash';
import { Input, Button } from '@weave/platform-ui-components';
import {
  DropdownField,
  PhoneIconLarge,
  SwitchField,
  Text,
  useFormField,
} from '@weave/design-system';
import { Typeahead } from 'react-bootstrap-typeahead';

import { LocationModel, selectCurrentLocation } from '../../../../redux/actions/location';
import { countryOptions, CountryCodes } from '../../../../constants/country-codes';
import { stateOptions } from '../../../../constants/state-codes';
import { AvailablePhoneNumbersTable } from './available-phone-numbers';
import { orderNewNumberStyles } from './order-new-number.styles';
import { Loader } from '../../../shared/loader/loader.component';
import { Page } from '../../../shared';
import { Store } from '../../../../redux/store/store.model';
import { ListAvailableNumbersRequest } from 'apis/porting/porting.types';
import { Provider } from '@weave/schema-gen-ts/dist/shared/porting/v1/enums.pb';
import { getAvailablePhoneNumbers } from '../../../../apis/phone-numbers/phone-numbers.api';
import { AvailablePhoneNumber } from '@weave/schema-gen-ts/dist/schemas/phone/number-service/v2/number_service.pb';
import {
  enableOutboundCallerIdForPurchasedNumbers,
  NumberToPurchase,
  purchaseNumbers,
} from '../purchase-number-utils';
import { theme } from '@weave/theme';
import { useAlert } from '@weave/alert-system';

interface Props {
  currentLocation: LocationModel | null;
}

interface StateOption {
  name: string;
  abbreviation: string;
}

const OrderNewNumber: React.FC<Props> = ({ currentLocation }) => {
  const [availableNumberRequest, setAvailableNumberRequest] =
    useState<ListAvailableNumbersRequest>({
      areaCode: '',
      city: '',
      state: '',
      zipCode: '',
      street: '',
      country: CountryCodes.USA,
      // Avoid setting the provider here as that's determined by the backend.
      provider: Provider.PROVIDER_UNSPECIFIED,
    });
  const [invalidState, setInvalidState] = useState(false);
  const [selectedNumbers, setSelectedNumbers] = useState<string[]>([]);
  const [availableNumbers, setAvailableNumbers] = useState<{
    availablePhoneNumbers: AvailablePhoneNumber[];
    uri: string;
  }>({
    availablePhoneNumbers: [],
    uri: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isPurchasing, setIsPurchasing] = useState(false);
  const alert = useAlert();

  const formatPhoneNumbers = (phoneNumbers: string[]) => {
    const maxDisplayed = 10;
    if (phoneNumbers.length <= maxDisplayed) {
      return phoneNumbers.join(', ');
    }

    const displayed = phoneNumbers.slice(0, maxDisplayed).join(', ');
    return `${displayed} and ${phoneNumbers.length - maxDisplayed} more`;
  };

  const countryField = useFormField({
    type: 'dropdown',
    value: availableNumberRequest.country || CountryCodes.USA,
  });

  const switchProps = useFormField({
    type: 'switch',
    value: true,
  });

  const outboundCallerSwitchProps = useFormField({
    type: 'switch',
    value: false,
  });

  const resetSearch = () => {
    setAvailableNumberRequest({
      areaCode: '',
      city: '',
      state: '',
      zipCode: '',
      street: '',
      country: CountryCodes.USA,
      provider: Provider.PROVIDER_UNSPECIFIED,
    });
    setSelectedNumbers([]);
    setInvalidState(false);
    setAvailableNumbers({ availablePhoneNumbers: [], uri: '' });
    setIsLoading(false);
  };

  useEffect(() => {
    if (countryField.value !== availableNumberRequest.country) {
      setAvailableNumberRequest((prev) => ({
        ...prev,
        country: countryField.value,
        // Clear state when country changes
        state: '',
      }));
      setInvalidState(false);
    }
  }, [countryField.value, availableNumberRequest.country]);

  const handleInputChange = (
    property: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const val = event.target.value;
    setAvailableNumberRequest((prev) => ({
      ...prev,
      [property]: val,
    }));
  };

  const getStateOptions = (): StateOption[] => {
    const selectedCountry = availableNumberRequest.country;
    if (selectedCountry === CountryCodes.Canada) {
      return stateOptions[CountryCodes.Canada].map((option) => ({
        name: option.label,
        abbreviation: option.value,
      }));
    }
    // Default to US states (and fallback if no country selected)
    return stateOptions[CountryCodes.USA].map((option) => ({
      name: option.label,
      abbreviation: option.value,
    }));
  };

  const handleStateBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const val = event.target.value;
    const currentStateOptions = getStateOptions();
    const invalidStateResult = !currentStateOptions.find((state) => state.name === val);
    setInvalidState(invalidStateResult);
  };

  const handleStateInputChange = (text: string) => {
    if (invalidState) {
      const currentStateOptions = getStateOptions();
      const invalidStateResult = !currentStateOptions.find(
        (state) => state.name === text
      );
      setInvalidState(invalidStateResult);
    }
  };

  const handleStateChange = (state: StateOption[]) => {
    const stateAbbreviation = state[0]?.abbreviation;
    setAvailableNumberRequest((prev) => ({
      ...prev,
      state: stateAbbreviation,
    }));
    setInvalidState(false);
  };

  const findAvailablePhoneNumbers = async (event: React.FormEvent) => {
    event.preventDefault();
    setSelectedNumbers([]);
    setIsLoading(true);
    setAvailableNumbers({ availablePhoneNumbers: [], uri: '' });

    try {
      const response = await getAvailablePhoneNumbers(availableNumberRequest);

      // Transform and store results
      const phoneNumbers = (response?.availablePhoneNumbers || []).map((phoneData) => ({
        phoneNumber: phoneData.phoneNumber || '',
        capabilities: phoneData.capabilities || {},
        provider:
          phoneData.provider === Provider.PROVIDER_UNSPECIFIED
            ? availableNumberRequest.country === CountryCodes.USA
              ? Provider.PROVIDER_BANDWIDTH
              : Provider.PROVIDER_TELNYX
            : phoneData.provider,
      }));

      setAvailableNumbers({
        availablePhoneNumbers: phoneNumbers || [],
        uri: `${(phoneNumbers || []).length} numbers found`,
      });
      setIsLoading(false);
    } catch (error) {
      console.error('Search failed:', error);
      setAvailableNumbers({ availablePhoneNumbers: [], uri: 'Search failed' });
      setIsLoading(false);
    }
  };

  useEffect(() => {
    resetSearch();
  }, [currentLocation?.LocationID]);

  const handlePurchaseChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const purchaseEnabled = e.target.checked;
    const phoneNumber = e.target.value;
    const newSelectedNumbers = cloneDeep(selectedNumbers);
    if (purchaseEnabled) {
      newSelectedNumbers.push(phoneNumber);
    } else {
      const index = newSelectedNumbers.indexOf(phoneNumber);
      newSelectedNumbers.splice(index, 1);
    }
    setSelectedNumbers(newSelectedNumbers);
  };

  const purchaseSelectedNumbers = (
    assignToDefaultCallRoute: boolean = true,
    enableOutboundCallerId: boolean = false
  ) => {
    const numbersToPurchase: NumberToPurchase[] = selectedNumbers.map((phoneNumber) => {
      const phoneNumberData = availableNumbers.availablePhoneNumbers.find(
        (num) => num.phoneNumber === phoneNumber
      );

      return {
        phoneNumber,
        provider: phoneNumberData?.provider,
      };
    });

    const runPurchaseNumbers = async () => {
      setIsPurchasing(true);

      try {
        const { purchasedNumbers, failures: purchaseFailures } = await purchaseNumbers({
          numbersToPurchase,
          assignToDefaultCallRoute,
        });

        const purchasedPhoneNumbers = purchasedNumbers
          .map((result) => result.requestedPhoneNumber)
          .filter(Boolean);
        const failedPhoneNumbers = purchaseFailures
          .map((failure) => failure.requestedPhoneNumber)
          .filter(Boolean);

        if (purchasedPhoneNumbers.length > 0) {
          alert.success(
            `Successfully purchased ${purchasedPhoneNumbers.length} number${
              purchasedPhoneNumbers.length === 1 ? '' : 's'
            }: ${formatPhoneNumbers(purchasedPhoneNumbers)}`
          );
        }

        if (purchaseFailures.length > 0) {
          console.error('Phone number purchase failures', purchaseFailures);
          alert.error(
            `Failed to purchase ${failedPhoneNumbers.length} number${
              failedPhoneNumbers.length === 1 ? '' : 's'
            }: ${formatPhoneNumbers(failedPhoneNumbers)}`
          );
        }

        if (enableOutboundCallerId) {
          const outboundFailures = await enableOutboundCallerIdForPurchasedNumbers(
            purchasedNumbers
          );

          if (outboundFailures.length > 0) {
            console.error('Outbound caller ID update failures', outboundFailures);
            alert.error(
              `Failed to enable outbound caller ID for ${
                outboundFailures.length
              } purchased number${outboundFailures.length === 1 ? '' : 's'}.`
            );
          }
        }
      } catch (error) {
        console.error('Unexpected phone number purchase error', error);
        alert.error('Unable to complete phone number purchase. Please try again.');
      } finally {
        resetSearch();
        setIsPurchasing(false);
      }
    };

    runPurchaseNumbers();
  };

  if (!currentLocation) {
    return null;
  }
  return (
    <Page
      title={
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>Purchase Voice/SMS Number</span>
          <PhoneIconLarge />
        </span>
      }
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <form
          className={css`
            display: grid;
            grid-template-columns: 100px repeat(auto-fit, minmax(100px, max-content));
            gap: 16px;
          `}
          autoComplete="off"
          onSubmit={findAvailablePhoneNumbers}
        >
          <Input
            type="text"
            placeholder="Area Code"
            value={availableNumberRequest.areaCode}
            onChange={(e) => handleInputChange('areaCode', e)}
            required
          />
          <DropdownField {...countryField} name="country" label="Country">
            {countryOptions.map((country) => (
              <DropdownField.Option key={country.value} value={country.value}>
                {country.label}
              </DropdownField.Option>
            ))}
          </DropdownField>
          {availableNumberRequest.country !== CountryCodes.USA && (
            <Typeahead
              id="twilio-number-state-dropdown"
              className={orderNewNumberStyles.typeaheadStyles}
              isInvalid={invalidState}
              onInputChange={handleStateInputChange}
              onBlur={handleStateBlur}
              options={getStateOptions()}
              placeholder="Province"
              highlightOnlyResult
              labelKey="name"
              selected={(() => {
                try {
                  const currentOptions = getStateOptions();
                  const currentState = availableNumberRequest.state;

                  if (!currentState || !currentOptions) {
                    return [];
                  }

                  const matchingOptions = currentOptions.filter(
                    (option) => option && option.abbreviation === currentState
                  );
                  return matchingOptions || [];
                } catch (error) {
                  console.error('Error filtering state options:', error);
                  return [];
                }
              })()}
              onChange={handleStateChange}
            />
          )}
          <Button disabled={isLoading} type="submit">
            {isLoading ? 'Searching...' : 'Search'}
          </Button>
        </form>
        {!isEmpty(availableNumbers) && (
          <>
            <div
              style={{
                textAlign: 'right',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              <div
                className={css`
                  border-radius: ${theme.borderRadius.medium};
                  background: ${theme.colors.primary5};
                  padding: ${theme.spacing(1.5)};
                  width: 770px;
                `}
              >
                <SwitchField
                  {...switchProps}
                  name="assignToDefaultCallRoute"
                  label={
                    <div>
                      <Text
                        weight="bold"
                        css={{ margin: 0, display: 'flex', justifyContent: 'flex-start' }}
                      >
                        Assign to Default Call Route
                      </Text>
                      <Text
                        size="medium"
                        css={{ margin: 0, display: 'flex', justifyContent: 'flex-start' }}
                      >
                        {switchProps.value
                          ? "The number(s) will be assigned to the location's default call route."
                          : 'The number(s) will not be automatically assigned allowing users to assign in call routes or device routes.'}
                      </Text>
                    </div>
                  }
                  labelPlacement="left"
                />
              </div>
              <div
                className={css`
                  border-radius: ${theme.borderRadius.medium};
                  background: ${theme.colors.primary5};
                  padding: ${theme.spacing(1.5)};
                  width: 770px;
                `}
              >
                <SwitchField
                  {...outboundCallerSwitchProps}
                  name="enableOutbound"
                  label={
                    <div>
                      <Text
                        weight="bold"
                        css={{ margin: 0, display: 'flex', justifyContent: 'flex-start' }}
                      >
                        Enable Outbound Caller ID Selection
                      </Text>
                      <Text
                        size="medium"
                        css={{ margin: 0, display: 'flex', justifyContent: 'flex-start' }}
                      >
                        {outboundCallerSwitchProps.value
                          ? 'Users will be able to select the number(s) for their caller ID when making calls from the Weave app.'
                          : 'Users will not be able to select the number(s) for their caller ID when making calls from the Weave app.'}
                      </Text>
                    </div>
                  }
                  labelPlacement="left"
                />
              </div>
            </div>

            <AvailablePhoneNumbersTable
              phoneNumbers={availableNumbers}
              subtitleText={availableNumbers.uri}
              selectedNumbers={selectedNumbers}
              handlePurchaseChange={handlePurchaseChange}
              loading={isPurchasing}
              onPurchaseClick={() =>
                purchaseSelectedNumbers(
                  switchProps.value,
                  outboundCallerSwitchProps.value
                )
              }
              showPurchaseButton
            />
          </>
        )}
        {isLoading && (
          <div className={orderNewNumberStyles.loaderStyles}>
            <Loader size="large" />
          </div>
        )}
      </div>
    </Page>
  );
};

const mapStateToProps = (state: Store) => {
  return {
    currentLocation: selectCurrentLocation(state),
  };
};

export default connect(mapStateToProps)(OrderNewNumber);
